import type { Campaign } from '~/modules/campaign/types';
import type { CampaignType } from '~/types/graphql';

import { removeDuplicates } from '~/utils/removeDuplicates';
import { deepCopy } from '~/utils';
import { create } from 'zustand';

export interface ICampaignStoreState {
  campaigns?: Campaign[];
  isQueryCampaignTemplate: boolean;
  isQueryCampaignPrice: boolean;
  isQueryCampaignUrl: boolean;
  isLoadingUpdateState: boolean;
}

export interface ICampaignStore<T extends Campaign = Campaign> extends ICampaignStoreState {
  setCampaigns: (campaigns?: T[]) => void;
  mergeCampaigns: (campaigns: T[]) => void;
  setCampaignsByType: (type: CampaignType, campaigns: T[]) => void;
  removeCampaign: (id: string) => void;
  upsertCampaign: (campaign: T) => void;
  upsertCampaigns: (campaigns: T[]) => void;
  getCampaignById: (id: string) => T | undefined;
  setIsQueryCampaignTemplate: (isQueryCampaignTemplate: boolean) => void;
  setIsQueryCampaignPrice: (isQueryCampaignPrice: boolean) => void;
  setIsQueryCampaignUrl: (isQueryCampaignUrl: boolean) => void;
  setIsLoadingUpdateState: (isLoadingUpdateState: boolean) => void;
}

export const useCampaignStore = create<ICampaignStore>((set, get) => ({
  campaigns: undefined,
  isQueryCampaignTemplate: false,
  isQueryCampaignPrice: false,
  isQueryCampaignUrl: false,
  isLoadingUpdateState: false,
  setCampaigns: (campaigns) => set({ campaigns }),
  mergeCampaigns: (campaigns) => {
    const currentCampaigns = deepCopy(get().campaigns) || [];
    const newCampaigns = [...currentCampaigns, ...campaigns];
    const newCampaignsDuplicated = removeDuplicates(newCampaigns, ['id']);

    set({ campaigns: newCampaignsDuplicated });
  },
  // Multiple bulk loaders (one per CampaignType) share this store. Each must only
  // replace its own type's slice -- a plain overwrite would wipe out whichever
  // other type's bulk fetch already landed, since the two requests race independently.
  setCampaignsByType: (type, campaigns) => {
    const currentCampaigns = deepCopy(get().campaigns) || [];
    const otherTypeCampaigns = currentCampaigns.filter((campaign) => campaign.type !== type);

    set({ campaigns: removeDuplicates([...otherTypeCampaigns, ...campaigns], ['id']) });
  },
  upsertCampaign: (campaign) => {
    const campaignCopy = deepCopy(campaign);
    const dataCampaigns = deepCopy(get().campaigns) || [];
    const currentCampaigns = removeDuplicates(dataCampaigns, ['id']);
    const updatedCampaigns = updateCampaign(currentCampaigns, campaignCopy);

    set({ campaigns: updatedCampaigns });
  },
  upsertCampaigns: (campaigns) => {
    const dataCampaigns = deepCopy(get().campaigns) || [];
    const currentCampaigns = removeDuplicates(dataCampaigns, ['id']);
    let updatedCampaigns = currentCampaigns;
    campaigns.forEach((campaign) => {
      updatedCampaigns = updateCampaign(updatedCampaigns, campaign);
    });

    set({ campaigns: updatedCampaigns });
  },
  removeCampaign: (id) => {
    const currentCampaigns = get().campaigns || [];
    set({ campaigns: currentCampaigns.filter((campaign) => campaign.id !== id) });
  },
  getCampaignById: (id) => {
    const campaigns = deepCopy(get().campaigns);
    return campaigns ? campaigns.find((c) => c.id === id) : undefined;
  },
  setIsQueryCampaignTemplate: (isQueryCampaignTemplate) => set({ isQueryCampaignTemplate }),
  setIsQueryCampaignPrice: (isQueryCampaignPrice) => set({ isQueryCampaignPrice }),
  setIsQueryCampaignUrl: (isQueryCampaignUrl) => set({ isQueryCampaignUrl }),
  setIsLoadingUpdateState: (isLoadingUpdateState) => set({ isLoadingUpdateState }),
}));

function updateCampaign(campaigns: Campaign[], campaign: Campaign): Campaign[] {
  const campaignCopy = deepCopy(campaign);
  const index = campaigns.findIndex((c) => c.id === campaignCopy.id);
  let updatedCampaigns;
  if (index !== -1) {
    // Update existing campaign
    updatedCampaigns = [...campaigns];
    updatedCampaigns[index] = { ...updatedCampaigns[index], ...campaignCopy };
  } else {
    // Insert new campaign
    updatedCampaigns = [...campaigns, campaignCopy];
  }

  return updatedCampaigns;
}
